import { useEffect, useState } from "react";
import { http, apiError, nf } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/Ui";
import UploadBox from "@/components/UploadBox";
import { Save } from "lucide-react";

const WAREHOUSES = ["Surabaya", "Jakarta", "Medan"];
const BRANCH_ONLY = ["Semarang", "Bandung", "Palembang", "Pekanbaru", "Makassar", "Banjarmasin", "Balikpapan"];
const KATEGORI = ["Produk Sudah Bergulir", "Produk Dalam Pengembangan", "Produk Baru Diversifikasi"];
const BULAN = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];

const empty = {
  periode: `${BULAN[new Date().getMonth()]} ${new Date().getFullYear()}`,
  kode_stock: "", deskripsi_produk: "", type_produk: "", spesifikasi_produk: "",
  kategori_pemasaran: KATEGORI[0], catatan: "",
};

export default function StockEntry() {
  const [f, setF] = useState(empty);
  const [g, setG] = useState({});
  const [c, setC] = useState({});
  const [busy, setBusy] = useState(false);
  const [products, setProducts] = useState([]);

  const load = () => http.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const total =
    Object.values(g).reduce((a, b) => a + (Number(b) || 0), 0) +
    Object.values(c).reduce((a, b) => a + (Number(b) || 0), 0);

  const autofill = async (kode) => {
    if (!kode) return;
    try {
      const { data } = await http.get(`/products/${kode.toUpperCase()}`);
      setF((s) => ({
        ...s,
        deskripsi_produk: data.deskripsi_produk || "",
        type_produk: data.type_produk || "",
        spesifikasi_produk: data.spesifikasi_produk || "",
        kategori_pemasaran: data.kategori_pemasaran || s.kategori_pemasaran,
      }));
      toast.success(`Data produk ${kode.toUpperCase()} terisi otomatis`);
    } catch {}
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await http.post("/stock", {
        ...f,
        kode_stock: f.kode_stock.toUpperCase(),
        gudang: Object.fromEntries(WAREHOUSES.map((w) => [w, Number(g[w]) || 0])),
        cabang: Object.fromEntries(BRANCH_ONLY.map((b) => [b, Number(c[b]) || 0])),
      });
      toast.success(`Tersimpan. Total stock nasional: ${nf(data.total_stock)}`);
      setF(empty); setG({}); setC({}); load();
    } catch (e2) {
      toast.error(apiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  const inp = "w-full h-10 px-3 border border-neutral-300 bg-white outline-none focus:ring-2 focus:ring-[#4338CA] focus:border-transparent text-sm";
  const numInp = inp + " mono text-right";
  const lbl = "text-[11px] font-semibold uppercase tracking-wide text-[#525252]";

  return (
    <div data-testid="stock-entry-page">
      <PageHeader subtitle="Dept Logistik · Kantor Pusat" title="Entry & Upload Data Stock" testId="stock-entry-header" />
      <div className="p-6 sm:p-8 space-y-4">
        <UploadBox endpoint="/stock/upload" templateKind="stock" onDone={load} testId="stock-upload" />

        <form onSubmit={submit} className="erp-panel p-5 space-y-5" data-testid="stock-entry-form">
          <div className="mono text-[11px] uppercase tracking-widest text-[#525252]">
            Entry Manual — Periode Awal / Tengah Bulan
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className={lbl}>Periode</label>
              <input data-testid="stock-periode-input" required className={inp} value={f.periode}
                onChange={(e) => setF({ ...f, periode: e.target.value })} placeholder="Juni 2026" />
            </div>
            <div>
              <label className={lbl}>Kode Stock</label>
              <input data-testid="stock-kode-input" required list="prodlist" className={inp + " uppercase"}
                value={f.kode_stock}
                onChange={(e) => setF({ ...f, kode_stock: e.target.value })}
                onBlur={(e) => autofill(e.target.value)} placeholder="KSFO022" />
              <datalist id="prodlist">
                {products.map((p) => <option key={p.kode_stock} value={p.kode_stock}>{p.deskripsi_produk}</option>)}
              </datalist>
            </div>
            <div>
              <label className={lbl}>Kategori Pemasaran Produk</label>
              <select data-testid="stock-kategori-select" className={inp} value={f.kategori_pemasaran}
                onChange={(e) => setF({ ...f, kategori_pemasaran: e.target.value })}>
                {KATEGORI.map((k) => <option key={k} value={k}>{k}</option>)}
              </select>
            </div>
            <div>
              <label className={lbl}>Deskripsi Produk</label>
              <input data-testid="stock-deskripsi-input" className={inp} value={f.deskripsi_produk}
                onChange={(e) => setF({ ...f, deskripsi_produk: e.target.value })} />
            </div>
            <div>
              <label className={lbl}>Type Produk</label>
              <input data-testid="stock-type-input" className={inp} value={f.type_produk}
                onChange={(e) => setF({ ...f, type_produk: e.target.value })} />
            </div>
            <div>
              <label className={lbl}>Spesifikasi Produk</label>
              <input data-testid="stock-spec-input" className={inp} value={f.spesifikasi_produk}
                onChange={(e) => setF({ ...f, spesifikasi_produk: e.target.value })} />
            </div>
          </div>

          <div>
            <div className={lbl + " mb-2"}>Qty Stock Gudang</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {WAREHOUSES.map((w) => (
                <div key={w}>
                  <label className="text-xs text-[#525252]">Gudang {w}</label>
                  <input data-testid={`stock-gudang-${w.toLowerCase()}-input`} type="number" className={numInp}
                    value={g[w] ?? ""} onChange={(e) => setG({ ...g, [w]: e.target.value })} placeholder="0" />
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className={lbl + " mb-2"}>Qty Stock Cabang</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
              {BRANCH_ONLY.map((b) => (
                <div key={b}>
                  <label className="text-xs text-[#525252]">{b}</label>
                  <input data-testid={`stock-cabang-${b.toLowerCase()}-input`} type="number" className={numInp}
                    value={c[b] ?? ""} onChange={(e) => setC({ ...c, [b]: e.target.value })} placeholder="0" />
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 border-t border-neutral-200 pt-4">
            <div className="flex-1">
              <div className={lbl}>Rekap Total Stock Nasional</div>
              <div data-testid="stock-total-display" className="mono text-2xl font-semibold">{nf(total)}</div>
            </div>
            <input data-testid="stock-catatan-input" className={inp + " sm:max-w-xs"} placeholder="Catatan"
              value={f.catatan} onChange={(e) => setF({ ...f, catatan: e.target.value })} />
            <button data-testid="stock-submit-button" disabled={busy}
              className="h-10 px-6 bg-[#4338CA] text-white text-xs uppercase tracking-wide flex items-center justify-center gap-2 hover:bg-[#3730A3] transition-colors disabled:opacity-60">
              <Save className="h-3.5 w-3.5" /> Simpan Data Stock
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
